import {
  clampPolygonClearOfSegments,
  clampPolygonInside,
  distance,
  localToWorld,
  pointInPolygon,
  polygonsOverlap,
  wallDirection,
} from "./geom";
import type {
  InteriorVertex,
  InteriorWall,
  Opening,
  Room,
  Vec2,
  WallMount,
} from "./types";

/** Endpoints within this many inches of a wall snap onto it and join. */
export const INTERIOR_TOUCH_IN = 6;

/** A stroke that stops short of the wall it points at reaches it from this far. */
export const INTERIOR_REACH_IN = 12;

/** Shortest wall the sketch tool will keep. */
export const INTERIOR_MIN_LENGTH_IN = 12;

/** Junctions stay this far from a host wall's corners so a segment can't collapse. */
const HOST_MARGIN_IN = 6;

const MIN_SEG_IN = 1;

/** Corners of an old and a new outline this close together are the same corner. */
const SAME_POINT_IN = 0.5;

type Hosted = {
  wallIndex: number;
  alongWall: number;
  width: number;
  interiorWallId?: string;
  interiorSegment?: number;
};

export interface WallSegment {
  labelIndex: number;
  kind: "boundary" | "interior";
  boundaryIndex?: number;
  wallId?: string;
  segment?: number;
  aId: string;
  bId: string;
  a: Vec2;
  b: Vec2;
  len: number;
}

export interface CommitInteriorResult {
  ok: true;
  room: Room;
  openings: Opening[];
  mounts: WallMount[];
  /** First new wall, so the editor can select it. */
  wallId: string;
  segmentCount: number;
  /** The stroke as joined, its ends fitted to the walls (what to preview). */
  stroke: Vec2[];
}

export interface CommitInteriorFailure {
  ok: false;
  message: string;
}

interface EdgeRec {
  aId: string;
  bId: string;
  kind: "boundary" | "interior";
  boundaryIndex?: number;
  wallId?: string;
  segment?: number;
}

interface Work {
  points: Vec2[];
  pointIds: string[];
  interiorVertices: InteriorVertex[];
  interiorWalls: InteriorWall[];
  openings: Opening[];
  mounts: WallMount[];
  lockedWallLengths?: Record<number, number>;
  lockedInteriorLengths?: Record<string, number>;
  /** Vertex ids that split a sketched chain (they lie on a pre-existing wall). */
  junctions: Set<string>;
}

function newId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

export function interiorLockKey(wallId: string, segment: number): string {
  return `${wallId}:${segment}`;
}

export function isInteriorHost(item: {
  interiorWallId?: string;
}): boolean {
  return !!item.interiorWallId;
}

/** A, B, … Z, AA, AB, … */
export function wallLabel(index: number): string {
  let n = index + 1;
  let s = "";
  while (n > 0) {
    n -= 1;
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26);
  }
  return s;
}

/**
 * Fill missing point ids with stable `p0`, `p1`, … so a reload doesn't mint
 * new ids (which would make every saved room look dirty). A length mismatch
 * means the ids no longer describe the outline, so they are rebuilt.
 */
export function ensurePointIds(room: Room): Room {
  if (room.pointIds && room.pointIds.length === room.points.length) return room;
  return {
    ...room,
    pointIds: room.points.map((_, i) => `p${i}`),
  };
}

export type WallHostRoom = Pick<
  Room,
  "points" | "pointIds" | "interiorVertices" | "interiorWalls"
>;

export function vertexPosition(room: WallHostRoom, id: string): Vec2 | null {
  const ids = room.pointIds ?? [];
  const bi = ids.indexOf(id);
  if (bi >= 0 && room.points[bi]) return room.points[bi];
  const v = (room.interiorVertices ?? []).find((x) => x.id === id);
  if (!v) return null;
  return { x: v.x, y: v.y };
}

function pos(work: Work, id: string): Vec2 | null {
  const bi = work.pointIds.indexOf(id);
  if (bi >= 0) return work.points[bi];
  const v = work.interiorVertices.find((x) => x.id === id);
  return v ? { x: v.x, y: v.y } : null;
}

export function interiorSegments(room: WallHostRoom): WallSegment[] {
  const walls = room.interiorWalls ?? [];
  const out: WallSegment[] = [];
  let label = room.points.length;
  for (const wall of walls) {
    const ids = wall.vertexIds;
    for (let s = 0; s < ids.length - 1; s++) {
      const a = vertexPosition(room, ids[s]);
      const b = vertexPosition(room, ids[s + 1]);
      if (!a || !b) continue;
      const len = distance(a, b);
      out.push({
        labelIndex: label,
        kind: "interior",
        wallId: wall.id,
        segment: s,
        aId: ids[s],
        bId: ids[s + 1],
        a,
        b,
        len,
      });
      label += 1;
    }
  }
  return out;
}

export function interiorSegmentCount(room: Room): number {
  return interiorSegments(room).length;
}

/** Outline edges first, then every interior segment. */
export function editableWalls(room: Room): WallSegment[] {
  const ensured = ensurePointIds(room);
  const ids = ensured.pointIds ?? [];
  const boundary: WallSegment[] = ensured.points.map((p, i) => {
    const next = ensured.points[(i + 1) % ensured.points.length];
    return {
      labelIndex: i,
      kind: "boundary" as const,
      boundaryIndex: i,
      aId: ids[i] ?? `p${i}`,
      bId: ids[(i + 1) % ids.length] ?? `p${(i + 1) % ensured.points.length}`,
      a: p,
      b: next,
      len: distance(p, next),
    };
  });
  return [...boundary, ...interiorSegments(ensured)];
}

export function resolveHostSegment(
  room: WallHostRoom,
  item: { wallIndex: number; interiorWallId?: string; interiorSegment?: number }
): { a: Vec2; b: Vec2; len: number } | null {
  if (item.interiorWallId != null && item.interiorSegment != null) {
    const seg = interiorSegments(room).find(
      (s) => s.wallId === item.interiorWallId && s.segment === item.interiorSegment
    );
    if (!seg || seg.len === 0) return null;
    return { a: seg.a, b: seg.b, len: seg.len };
  }
  const n = room.points.length;
  if (item.wallIndex < 0 || item.wallIndex >= n) return null;
  const a = room.points[item.wallIndex];
  const b = room.points[(item.wallIndex + 1) % n];
  if (!a || !b) return null;
  const len = distance(a, b);
  if (len === 0) return null;
  return { a, b, len };
}

export function hostsMatch(
  a: { wallIndex: number; interiorWallId?: string; interiorSegment?: number },
  b: { wallIndex: number; interiorWallId?: string; interiorSegment?: number }
): boolean {
  if (a.interiorWallId || b.interiorWallId) {
    return (
      a.interiorWallId === b.interiorWallId && a.interiorSegment === b.interiorSegment
    );
  }
  return a.wallIndex === b.wallIndex;
}

export function interiorObstacleSegments(room: Room): { a: Vec2; b: Vec2 }[] {
  return interiorSegments(room)
    .filter((s) => s.len > 0)
    .map((s) => ({ a: s.a, b: s.b }));
}

/** Keep a footprint inside the outline and off interior walls. */
export function clampPoseInRoom(
  center: Vec2,
  localOutline: Vec2[],
  rotationDeg: number,
  room: Room
): Vec2 {
  const polygon = room.points;
  let c = clampPolygonInside(center, localOutline, rotationDeg, polygon);
  const segs = interiorObstacleSegments(room);
  if (segs.length > 0) {
    const thickness = Math.max(room.wallThickness || 0, 4);
    c = clampPolygonClearOfSegments(c, localOutline, rotationDeg, segs, thickness);
    c = clampPolygonInside(c, localOutline, rotationDeg, polygon);
  }
  return c;
}

/** True when the footprint crosses an interior wall's thickness. */
export function footprintCrossesInteriorWall(
  center: Vec2,
  localOutline: Vec2[],
  rotationDeg: number,
  room: Room
): boolean {
  const segs = interiorObstacleSegments(room);
  if (segs.length === 0 || localOutline.length < 3) return false;
  const world = localToWorld(localOutline, center.x, center.y, rotationDeg);
  const thickness = Math.max(room.wallThickness || 0, 4);
  for (const seg of segs) {
    if (polygonsOverlap(world, thickenedRect(seg.a, seg.b, thickness))) return true;
  }
  return false;
}

function thickenedRect(a: Vec2, b: Vec2, thickness: number): Vec2[] {
  const dir = wallDirection(a, b);
  const nx = -dir.y;
  const ny = dir.x;
  const h = thickness / 2;
  return [
    { x: a.x + nx * h, y: a.y + ny * h },
    { x: b.x + nx * h, y: b.y + ny * h },
    { x: b.x - nx * h, y: b.y - ny * h },
    { x: a.x - nx * h, y: a.y - ny * h },
  ];
}

function boundaryReferences(room: Room, vertexId: string): boolean {
  return (room.interiorWalls ?? []).some((w) => w.vertexIds.includes(vertexId));
}

export function canRemoveBoundaryVertex(room: Room, index: number): boolean {
  if (room.points.length <= 3) return false;
  const id = room.pointIds?.[index];
  if (id && boundaryReferences(room, id)) return false;
  return true;
}

function edgesOf(work: Work): EdgeRec[] {
  const out: EdgeRec[] = [];
  const n = work.points.length;
  for (let i = 0; i < n; i++) {
    out.push({
      aId: work.pointIds[i],
      bId: work.pointIds[(i + 1) % n],
      kind: "boundary",
      boundaryIndex: i,
    });
  }
  for (const wall of work.interiorWalls) {
    for (let s = 0; s < wall.vertexIds.length - 1; s++) {
      out.push({
        aId: wall.vertexIds[s],
        bId: wall.vertexIds[s + 1],
        kind: "interior",
        wallId: wall.id,
        segment: s,
      });
    }
  }
  return out;
}

function edgeGeom(work: Work, edge: EdgeRec): { a: Vec2; b: Vec2; len: number } | null {
  const a = pos(work, edge.aId);
  const b = pos(work, edge.bId);
  if (!a || !b) return null;
  return { a, b, len: distance(a, b) };
}

function lerp(a: Vec2, b: Vec2, t: number): Vec2 {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

function projectT(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return 0;
  return ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
}

function distToSeg(p: Vec2, a: Vec2, b: Vec2): { d: number; t: number; point: Vec2 } {
  const t = Math.max(0, Math.min(1, projectT(p, a, b)));
  const point = lerp(a, b, t);
  return { d: distance(p, point), t, point };
}

function pointInOrOn(p: Vec2, polygon: Vec2[], tol: number): boolean {
  if (polygon.length >= 3 && pointInPolygon(p, polygon)) return true;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    if (distToSeg(p, a, b).d <= tol) return true;
  }
  return false;
}

function remapBoundaryHosts<T extends Hosted>(
  items: T[],
  edgeIndex: number,
  firstLen: number
): T[] {
  return items.map((item) => {
    if (item.interiorWallId) return item;
    if (item.wallIndex > edgeIndex) {
      return { ...item, wallIndex: item.wallIndex + 1 };
    }
    if (item.wallIndex !== edgeIndex) return item;
    if (item.alongWall > firstLen + 1e-6) {
      return {
        ...item,
        wallIndex: edgeIndex + 1,
        alongWall: item.alongWall - firstLen,
      };
    }
    return item;
  });
}

function remapBoundaryLocks(
  locks: Record<number, number> | undefined,
  edgeIndex: number,
  firstLen: number
): Record<number, number> | undefined {
  if (!locks) return locks;
  const next: Record<number, number> = {};
  for (const [k, v] of Object.entries(locks)) {
    const idx = Number(k);
    if (!Number.isFinite(idx)) continue;
    if (idx > edgeIndex) next[idx + 1] = v;
    else if (idx === edgeIndex) next[idx] = firstLen;
    else next[idx] = v;
  }
  return next;
}

function remapInteriorHosts<T extends Hosted>(
  items: T[],
  wallId: string,
  segment: number,
  firstLen: number
): T[] {
  return items.map((item) => {
    if (item.interiorWallId !== wallId || item.interiorSegment == null) return item;
    if (item.interiorSegment > segment) {
      return { ...item, interiorSegment: item.interiorSegment + 1 };
    }
    if (item.interiorSegment !== segment) return item;
    if (item.alongWall > firstLen + 1e-6) {
      return {
        ...item,
        interiorSegment: segment + 1,
        alongWall: item.alongWall - firstLen,
      };
    }
    return item;
  });
}

function remapInteriorLocks(
  locks: Record<string, number> | undefined,
  wallId: string,
  segment: number,
  firstLen: number
): Record<string, number> | undefined {
  if (!locks) return locks;
  const next: Record<string, number> = {};
  for (const [k, v] of Object.entries(locks)) {
    const split = k.split(":");
    const id = split[0];
    const seg = Number(split[1]);
    if (id !== wallId || !Number.isFinite(seg)) {
      next[k] = v;
      continue;
    }
    if (seg > segment) next[interiorLockKey(id, seg + 1)] = v;
    else if (seg === segment) next[interiorLockKey(id, seg)] = firstLen;
    else next[k] = v;
  }
  return next;
}

/** Split an outline edge at `t` and return the new vertex id. */
function splitBoundary(work: Work, edgeIndex: number, t: number): string {
  const n = work.points.length;
  const a = work.points[edgeIndex];
  const b = work.points[(edgeIndex + 1) % n];
  const len = distance(a, b);
  const tt = len === 0 ? 0.5 : Math.max(MIN_SEG_IN / len, Math.min(1 - MIN_SEG_IN / len, t));
  const point = lerp(a, b, tt);
  const id = newId("p");
  const firstLen = distance(a, point);
  work.points.splice(edgeIndex + 1, 0, point);
  work.pointIds.splice(edgeIndex + 1, 0, id);
  work.openings = remapBoundaryHosts(work.openings, edgeIndex, firstLen);
  work.mounts = remapBoundaryHosts(work.mounts, edgeIndex, firstLen);
  work.lockedWallLengths = remapBoundaryLocks(work.lockedWallLengths, edgeIndex, firstLen);
  work.junctions.add(id);
  return id;
}

/** Split an interior segment at `t` and return the new vertex id. */
function splitInterior(work: Work, wallId: string, segment: number, t: number): string {
  const wall = work.interiorWalls.find((w) => w.id === wallId);
  if (!wall) return "";
  const aId = wall.vertexIds[segment];
  const bId = wall.vertexIds[segment + 1];
  const a = pos(work, aId);
  const b = pos(work, bId);
  if (!a || !b) return aId;
  const len = distance(a, b);
  const tt = len === 0 ? 0.5 : Math.max(MIN_SEG_IN / len, Math.min(1 - MIN_SEG_IN / len, t));
  const point = lerp(a, b, tt);
  const id = newId("v");
  work.interiorVertices.push({ id, x: point.x, y: point.y });
  wall.vertexIds.splice(segment + 1, 0, id);
  const firstLen = distance(a, point);
  work.openings = remapInteriorHosts(work.openings, wallId, segment, firstLen);
  work.mounts = remapInteriorHosts(work.mounts, wallId, segment, firstLen);
  work.lockedInteriorLengths = remapInteriorLocks(
    work.lockedInteriorLengths,
    wallId,
    segment,
    firstLen
  );
  work.junctions.add(id);
  return id;
}

function nearestVertex(work: Work, p: Vec2, tol: number): string | null {
  let best: string | null = null;
  let bestD = tol;
  for (let i = 0; i < work.pointIds.length; i++) {
    const d = distance(p, work.points[i]);
    if (d <= bestD) {
      bestD = d;
      best = work.pointIds[i];
    }
  }
  for (const v of work.interiorVertices) {
    const d = distance(p, v);
    if (d <= bestD) {
      bestD = d;
      best = v.id;
    }
  }
  return best;
}

/**
 * Snap `p` onto existing geometry. Returns a vertex id, or null when the
 * point is outside the room and not close enough to a wall to join.
 */
function snapToGeometry(work: Work, p: Vec2, polygon: Vec2[]): string | null {
  const nearV = nearestVertex(work, p, INTERIOR_TOUCH_IN);
  let nearVD = Infinity;
  if (nearV) {
    const vp = pos(work, nearV);
    if (vp) nearVD = distance(p, vp);
  }
  let best: { edge: EdgeRec; t: number; d: number } | null = null;
  for (const edge of edgesOf(work)) {
    const g = edgeGeom(work, edge);
    if (!g || g.len < MIN_SEG_IN) continue;
    const hit = distToSeg(p, g.a, g.b);
    if (hit.d > INTERIOR_TOUCH_IN) continue;
    const gap = MIN_SEG_IN / g.len;
    if (hit.t <= gap || hit.t >= 1 - gap) continue;
    if (!best || hit.d < best.d) best = { edge, t: hit.t, d: hit.d };
  }
  if (nearV && (!best || nearVD <= best.d)) {
    work.junctions.add(nearV);
    return nearV;
  }
  if (best) {
    if (best.edge.kind === "boundary" && best.edge.boundaryIndex != null) {
      return splitBoundary(work, best.edge.boundaryIndex, best.t);
    }
    if (best.edge.wallId != null && best.edge.segment != null) {
      return splitInterior(work, best.edge.wallId, best.edge.segment, best.t);
    }
  }
  if (!pointInOrOn(p, polygon, 1)) return null;
  const id = newId("v");
  work.interiorVertices.push({ id, x: p.x, y: p.y });
  return id;
}

function intersectProper(
  a: Vec2,
  b: Vec2,
  c: Vec2,
  d: Vec2
): { t: number; u: number; point: Vec2 } | null {
  const rx = b.x - a.x;
  const ry = b.y - a.y;
  const sx = d.x - c.x;
  const sy = d.y - c.y;
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) < 1e-9) return null;
  const qpx = c.x - a.x;
  const qpy = c.y - a.y;
  const t = (qpx * sy - qpy * sx) / denom;
  const u = (qpx * ry - qpy * rx) / denom;
  const eps = 1e-4;
  if (t <= eps || t >= 1 - eps || u <= eps || u >= 1 - eps) return null;
  return { t, u, point: { x: a.x + t * rx, y: a.y + t * ry } };
}

interface Proposed {
  aId: string;
  bId: string;
}

function sameUndirected(a: string, b: string, c: string, d: string): boolean {
  return (a === c && b === d) || (a === d && b === c);
}

/**
 * Join an open polyline into the room. Touching or crossing a wall splits
 * both and inserts one shared corner.
 */
export function commitInteriorWall(
  room: Room,
  polyline: Vec2[],
  openings: Opening[],
  mounts: WallMount[]
): CommitInteriorResult | CommitInteriorFailure {
  if (polyline.length < 2 || room.points.length < 3) {
    return { ok: false, message: "Draw the wall inside the room." };
  }
  const base = ensurePointIds(room);
  const work: Work = {
    points: base.points.map((p) => ({ ...p })),
    pointIds: [...(base.pointIds ?? [])],
    interiorVertices: (base.interiorVertices ?? []).map((v) => ({ ...v })),
    interiorWalls: (base.interiorWalls ?? []).map((w) => ({
      id: w.id,
      vertexIds: [...w.vertexIds],
    })),
    openings: openings.map((o) => ({ ...o })),
    mounts: mounts.map((m) => ({ ...m })),
    lockedWallLengths: base.lockedWallLengths
      ? { ...base.lockedWallLengths }
      : undefined,
    lockedInteriorLengths: base.lockedInteriorLengths
      ? { ...base.lockedInteriorLengths }
      : undefined,
    junctions: new Set<string>(),
  };
  // Existing corners are junctions so a stroke that passes through one splits.
  for (const id of work.pointIds) work.junctions.add(id);
  for (const v of work.interiorVertices) work.junctions.add(v.id);

  const polygon = work.points.map((p) => ({ ...p }));
  const stroke = reachWalls(work, polyline, polygon);
  if (!stroke) return { ok: false, message: "Draw the wall inside the room." };
  const snapped: string[] = [];
  for (const p of stroke) {
    const id = snapToGeometry(work, p, polygon);
    if (!id) return { ok: false, message: "Draw the wall inside the room." };
    if (snapped[snapped.length - 1] !== id) snapped.push(id);
  }
  if (snapped.length < 2) {
    return {
      ok: false,
      message: "That stroke follows an existing wall — draw across the room.",
    };
  }

  let proposed: Proposed[] = [];
  for (let i = 0; i < snapped.length - 1; i++) {
    proposed.push({ aId: snapped[i], bId: snapped[i + 1] });
  }

  // Reject chords that leave the room (an L-shape's exterior, for example).
  for (const seg of proposed) {
    const a = pos(work, seg.aId);
    const b = pos(work, seg.bId);
    if (!a || !b) return { ok: false, message: "Draw the wall inside the room." };
    for (let s = 1; s < 5; s++) {
      const p = lerp(a, b, s / 5);
      if (!pointInOrOn(p, polygon, 1)) {
        return { ok: false, message: "Draw the wall inside the room." };
      }
    }
  }

  let guard = 0;
  while (guard++ < 64) {
    const hit = findCrossing(work, proposed);
    if (!hit) break;
    if (hit.kind === "edge") {
      const id =
        hit.edge.kind === "boundary" && hit.edge.boundaryIndex != null
          ? splitBoundary(work, hit.edge.boundaryIndex, hit.u)
          : hit.edge.wallId != null && hit.edge.segment != null
            ? splitInterior(work, hit.edge.wallId, hit.edge.segment, hit.u)
            : "";
      if (!id) break;
      splitProposed(proposed, hit.proposedIndex, id);
    } else {
      const id = newId("v");
      const point = hit.point;
      work.interiorVertices.push({ id, x: point.x, y: point.y });
      work.junctions.add(id);
      // Split the higher index first so the lower splice doesn't shift it.
      const [lo, hi] = hit.i < hit.j ? [hit.i, hit.j] : [hit.j, hit.i];
      splitProposed(proposed, hi, id);
      splitProposed(proposed, lo, id);
    }
  }

  const existing = edgesOf(work);
  const kept: Proposed[] = [];
  for (const seg of proposed) {
    if (seg.aId === seg.bId) continue;
    const a = pos(work, seg.aId);
    const b = pos(work, seg.bId);
    if (!a || !b || distance(a, b) < MIN_SEG_IN) continue;
    const duplicate = existing.some((e) =>
      sameUndirected(e.aId, e.bId, seg.aId, seg.bId)
    );
    if (duplicate) continue;
    kept.push(seg);
  }
  if (kept.length === 0) {
    return {
      ok: false,
      message: "That stroke follows an existing wall — draw across the room.",
    };
  }

  const chains = chainSegments(kept, work.junctions);
  const created: InteriorWall[] = [];
  for (const chain of chains) {
    if (chain.length < 2) continue;
    let len = 0;
    for (let i = 0; i < chain.length - 1; i++) {
      const a = pos(work, chain[i]);
      const b = pos(work, chain[i + 1]);
      if (a && b) len += distance(a, b);
    }
    if (len < MIN_SEG_IN) continue;
    const wall: InteriorWall = { id: newId("iw"), vertexIds: chain };
    work.interiorWalls.push(wall);
    created.push(wall);
  }
  if (created.length === 0) {
    return {
      ok: false,
      message: "That stroke follows an existing wall — draw across the room.",
    };
  }

  const next = roomFromWork(base, work);
  const segments = created.reduce((n, w) => n + Math.max(0, w.vertexIds.length - 1), 0);
  return {
    ok: true,
    room: next,
    openings: work.openings,
    mounts: work.mounts,
    wallId: created[0].id,
    segmentCount: segments,
    stroke,
  };
}

/**
 * Fit a stroke's two ends to the room. An end past the outline is cut back to
 * where the stroke leaves the room; an end that stops short of the wall it
 * points at is carried onto it (up to {@link INTERIOR_REACH_IN}). Ends already
 * near a wall are left to the snap, and ends out in the open stay free. Null
 * when an end can't be brought inside.
 */
function reachWalls(work: Work, polyline: Vec2[], polygon: Vec2[]): Vec2[] | null {
  const line = polyline.map((p) => ({ ...p }));
  const last = line.length - 1;
  if (last < 1) return null;
  const ends: [number, number][] = [
    [last, last - 1],
    [0, 1],
  ];
  for (const [end, prev] of ends) {
    const tip = line[end];
    const from = line[prev];
    if (!pointInOrOn(tip, polygon, 1)) {
      const exit = lastCrossing(from, tip, polygon);
      if (!exit) return null;
      line[end] = exit;
    } else if (!nearEdge(work, tip, INTERIOR_TOUCH_IN)) {
      const hit = reachAlong(work, from, tip, INTERIOR_REACH_IN);
      if (hit) line[end] = hit;
    }
  }
  return line;
}

/** Where segment p→q meets segment a→b, ends included; `t` runs along p→q. */
function segmentHit(p: Vec2, q: Vec2, a: Vec2, b: Vec2): { t: number; point: Vec2 } | null {
  const rx = q.x - p.x;
  const ry = q.y - p.y;
  const sx = b.x - a.x;
  const sy = b.y - a.y;
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) < 1e-9) return null;
  const qpx = a.x - p.x;
  const qpy = a.y - p.y;
  const t = (qpx * sy - qpy * sx) / denom;
  const u = (qpx * ry - qpy * rx) / denom;
  const eps = 1e-6;
  if (t < -eps || t > 1 + eps || u < -eps || u > 1 + eps) return null;
  return { t, point: { x: p.x + t * rx, y: p.y + t * ry } };
}

/** The last point where from→tip crosses the outline. */
function lastCrossing(from: Vec2, tip: Vec2, polygon: Vec2[]): Vec2 | null {
  let best: { t: number; point: Vec2 } | null = null;
  for (let i = 0; i < polygon.length; i++) {
    const hit = segmentHit(from, tip, polygon[i], polygon[(i + 1) % polygon.length]);
    if (hit && (!best || hit.t > best.t)) best = hit;
  }
  return best?.point ?? null;
}

function nearEdge(work: Work, p: Vec2, tol: number): boolean {
  return edgesOf(work).some((edge) => {
    const g = edgeGeom(work, edge);
    return !!g && distToSeg(p, g.a, g.b).d <= tol;
  });
}

/** The first wall straight ahead of `tip`, heading from→tip, within `max` inches. */
function reachAlong(work: Work, from: Vec2, tip: Vec2, max: number): Vec2 | null {
  const len = distance(from, tip);
  if (len < MIN_SEG_IN) return null;
  const ahead = {
    x: tip.x + ((tip.x - from.x) / len) * max,
    y: tip.y + ((tip.y - from.y) / len) * max,
  };
  let best: { t: number; point: Vec2 } | null = null;
  for (const edge of edgesOf(work)) {
    const g = edgeGeom(work, edge);
    if (!g || g.len < MIN_SEG_IN) continue;
    const hit = segmentHit(tip, ahead, g.a, g.b);
    if (hit && (!best || hit.t < best.t)) best = hit;
  }
  return best?.point ?? null;
}

function splitProposed(proposed: Proposed[], index: number, id: string) {
  const seg = proposed[index];
  if (!seg || seg.aId === id || seg.bId === id) return;
  proposed.splice(index, 1, { aId: seg.aId, bId: id }, { aId: id, bId: seg.bId });
}

function findCrossing(
  work: Work,
  proposed: Proposed[]
):
  | { kind: "edge"; proposedIndex: number; edge: EdgeRec; u: number; t: number }
  | { kind: "self"; i: number; j: number; t: number; u: number; point: Vec2 }
  | null {
  const existing = edgesOf(work);
  for (let i = 0; i < proposed.length; i++) {
    const pa = pos(work, proposed[i].aId);
    const pb = pos(work, proposed[i].bId);
    if (!pa || !pb) continue;
    for (const edge of existing) {
      if (
        edge.aId === proposed[i].aId ||
        edge.aId === proposed[i].bId ||
        edge.bId === proposed[i].aId ||
        edge.bId === proposed[i].bId
      ) {
        continue;
      }
      const g = edgeGeom(work, edge);
      if (!g) continue;
      const hit = intersectProper(pa, pb, g.a, g.b);
      if (hit) {
        return { kind: "edge", proposedIndex: i, edge, u: hit.u, t: hit.t };
      }
    }
  }
  for (let i = 0; i < proposed.length; i++) {
    for (let j = i + 1; j < proposed.length; j++) {
      const a = proposed[i];
      const b = proposed[j];
      if (a.aId === b.aId || a.aId === b.bId || a.bId === b.aId || a.bId === b.bId) {
        continue;
      }
      const pa = pos(work, a.aId);
      const pb = pos(work, a.bId);
      const pc = pos(work, b.aId);
      const pd = pos(work, b.bId);
      if (!pa || !pb || !pc || !pd) continue;
      const hit = intersectProper(pa, pb, pc, pd);
      if (hit) return { kind: "self", i, j, t: hit.t, u: hit.u, point: hit.point };
    }
  }
  return null;
}

function chainSegments(segs: Proposed[], junctions: Set<string>): string[][] {
  const chains: string[][] = [];
  let current: string[] = [];
  for (const seg of segs) {
    if (current.length === 0) {
      current = [seg.aId, seg.bId];
      continue;
    }
    const tail = current[current.length - 1];
    if (tail === seg.aId) {
      // A junction in the middle of the stroke starts a new wall that shares it.
      if (junctions.has(tail) && current.length >= 2) {
        chains.push(current);
        current = [seg.aId, seg.bId];
      } else {
        current.push(seg.bId);
      }
    } else {
      if (current.length >= 2) chains.push(current);
      current = [seg.aId, seg.bId];
    }
  }
  if (current.length >= 2) chains.push(current);
  return chains;
}

function roomFromWork(base: Room, work: Work): Room {
  const room: Room = {
    ...base,
    points: work.points,
    pointIds: work.pointIds,
    interiorVertices: work.interiorVertices,
    interiorWalls: work.interiorWalls,
  };
  putLockMap(room, "lockedWallLengths", work.lockedWallLengths);
  putLockMap(room, "lockedInteriorLengths", work.lockedInteriorLengths);
  return room;
}

/** Keep a lock map only while it has entries. An explicit `undefined` is
 *  what Firestore rejects when the whole room is written. */
function putLockMap(
  room: Room,
  key: "lockedWallLengths" | "lockedInteriorLengths",
  value: Room["lockedWallLengths"] | Room["lockedInteriorLengths"]
): void {
  if (value && Object.keys(value).length > 0) {
    if (key === "lockedWallLengths") {
      room.lockedWallLengths = value as Record<number, number>;
    } else {
      room.lockedInteriorLengths = value as Record<string, number>;
    }
  } else {
    delete room[key];
  }
}

/** Drop interior walls and anything mounted on them. Outline openings stay. */
export function clearInteriorGeometry(
  room: Room,
  openings: Opening[],
  mounts: WallMount[],
  points: Vec2[] = room.points
): { room: Room; openings: Opening[]; mounts: WallMount[] } {
  const next: Room = {
    ...room,
    points,
    pointIds: points.map((_, i) => `p${i}`),
    interiorVertices: [],
    interiorWalls: [],
  };
  delete next.lockedInteriorLengths;
  if (points.length !== room.points.length) {
    delete next.lockedWallLengths;
  }
  return {
    room: next,
    openings: openings.filter((o) => !o.interiorWallId),
    mounts: mounts.filter((m) => !m.interiorWallId),
  };
}

/**
 * Swap in a grown outline (an addition unioned into the room) and keep what
 * was built on the old one. Corners that stay keep their ids, junctions the
 * union merged into a straight edge come back (or float free where the
 * addition opened the wall), and interior walls stay. Outline openings and
 * mounts on a wall the addition didn't touch stay put; the rest move to the
 * edge under their old position.
 */
export function growOutline(
  room: Room,
  openings: Opening[],
  mounts: WallMount[],
  outline: Vec2[]
): { room: Room; openings: Opening[]; mounts: WallMount[] } {
  const base = ensurePointIds(room);
  const oldPoints = base.points;
  const oldIds = base.pointIds ?? [];
  const ring = outline.map((p) => ({ ...p }));
  if (signedArea(ring) * signedArea(oldPoints) < 0) ring.reverse();
  const ids = ring.map((p) => {
    const i = oldPoints.findIndex((q) => distance(p, q) <= SAME_POINT_IN);
    return i >= 0 ? oldIds[i] : "";
  });

  const joined = new Set<string>();
  for (const wall of base.interiorWalls ?? []) {
    for (const id of wall.vertexIds) joined.add(id);
  }
  const freed: InteriorVertex[] = [];
  oldPoints.forEach((p, i) => {
    const id = oldIds[i];
    if (!joined.has(id) || ids.includes(id)) return;
    const k = ring.findIndex((a, j) => {
      const hit = distToSeg(p, a, ring[(j + 1) % ring.length]);
      return hit.d <= SAME_POINT_IN && hit.t > 0 && hit.t < 1;
    });
    if (k < 0) {
      freed.push({ id, x: p.x, y: p.y });
      return;
    }
    ring.splice(k + 1, 0, { ...p });
    ids.splice(k + 1, 0, id);
  });

  // Start where the old outline started so wall letters stay put.
  const start = oldIds.map((id) => ids.indexOf(id)).find((i) => i >= 0) ?? 0;
  const points = [...ring.slice(start), ...ring.slice(0, start)];
  const pointIds = [...ids.slice(start), ...ids.slice(0, start)].map(
    (id) => id || newId("p")
  );

  const sameEdge = (index: number) =>
    edgeIndexOf(pointIds, oldIds[index], oldIds[(index + 1) % oldIds.length]);

  const rehost = <T extends Hosted>(item: T): T => {
    if (item.interiorWallId) return item;
    const kept = sameEdge(item.wallIndex);
    if (kept >= 0) return { ...item, wallIndex: kept };
    const a = oldPoints[item.wallIndex];
    const b = oldPoints[(item.wallIndex + 1) % oldPoints.length];
    if (!a || !b || distance(a, b) === 0) return item;
    const dir = wallDirection(a, b);
    const center = { x: a.x + dir.x * item.alongWall, y: a.y + dir.y * item.alongWall };
    const host = edgeUnder(points, center, dir);
    if (!host) return item;
    const half = Math.min(item.width / 2, host.len / 2);
    return {
      ...item,
      wallIndex: host.index,
      alongWall: Math.max(half, Math.min(host.len - half, host.along)),
    };
  };

  let locks: Record<number, number> | undefined;
  for (const [k, v] of Object.entries(base.lockedWallLengths ?? {})) {
    const j = sameEdge(Number(k));
    if (j >= 0) (locks ??= {})[j] = v;
  }

  const next: Room = {
    ...base,
    points,
    pointIds,
    interiorVertices: [...(base.interiorVertices ?? []), ...freed],
    shapeKind: "custom",
  };
  putLockMap(next, "lockedWallLengths", locks);
  return { room: next, openings: openings.map(rehost), mounts: mounts.map(rehost) };
}

/**
 * The outline edge for something centered at `center` on a wall running `dir`:
 * the same wall if it's still there, else the wall straight behind it (an
 * addition's back wall), else the nearest.
 */
function edgeUnder(
  points: Vec2[],
  center: Vec2,
  dir: Vec2
): { index: number; along: number; len: number } | null {
  let best: { index: number; along: number; len: number; score: number } | null = null;
  for (let k = 0; k < points.length; k++) {
    const a = points[k];
    const b = points[(k + 1) % points.length];
    const len = distance(a, b);
    if (len < MIN_SEG_IN) continue;
    const d = wallDirection(a, b);
    const sameWay = d.x * dir.x + d.y * dir.y > 0.999;
    const hit = distToSeg(center, a, b);
    const t = projectT(center, a, b);
    const tier = sameWay && hit.d <= SAME_POINT_IN ? 0 : sameWay && t >= 0 && t <= 1 ? 1 : 2;
    const score = tier * 1e6 + hit.d;
    if (!best || score < best.score) best = { index: k, along: hit.t * len, len, score };
  }
  return best;
}

/** Index of the outline edge running from corner `aId` to corner `bId`, or -1. */
function edgeIndexOf(ids: string[], aId: string | undefined, bId: string | undefined): number {
  if (!aId || !bId) return -1;
  return ids.findIndex((id, i) => id === aId && ids[(i + 1) % ids.length] === bId);
}

function signedArea(points: Vec2[]): number {
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    sum += a.x * b.y - b.x * a.y;
  }
  return sum / 2;
}

export function splitBoundaryEdge(
  room: Room,
  openings: Opening[],
  mounts: WallMount[],
  edgeIndex: number,
  t = 0.5
): { room: Room; openings: Opening[]; mounts: WallMount[]; vertexIndex: number } | null {
  const base = ensurePointIds(room);
  if (edgeIndex < 0 || edgeIndex >= base.points.length) return null;
  const work = toWork(base, openings, mounts);
  splitBoundary(work, edgeIndex, t);
  return {
    room: roomFromWork(base, work),
    openings: work.openings,
    mounts: work.mounts,
    vertexIndex: edgeIndex + 1,
  };
}

export function removeBoundaryVertex(
  room: Room,
  openings: Opening[],
  mounts: WallMount[],
  index: number
): { room: Room; openings: Opening[]; mounts: WallMount[] } | null {
  const n = room.points.length;
  if (n <= 3 || index < 0 || index >= n) return null;
  if (!canRemoveBoundaryVertex(room, index)) return null;
  const base = ensurePointIds(room);
  const prev = (index - 1 + n) % n;
  const prevLen = distance(base.points[prev], base.points[index]);
  const merged = index === 0 ? n - 2 : index - 1;
  const points = base.points.filter((_, i) => i !== index);
  const pointIds = (base.pointIds ?? []).filter((_, i) => i !== index);

  const mapEdge = (edge: number): { index: number; alongAdd: number } | null => {
    if (edge === index) return { index: merged, alongAdd: prevLen };
    if (edge === prev) return { index: merged, alongAdd: 0 };
    if (edge > index) return { index: edge - 1, alongAdd: 0 };
    return { index: edge, alongAdd: 0 };
  };

  const openingsNext = openings.map((o) => {
    if (o.interiorWallId) return o;
    const mapped = mapEdge(o.wallIndex);
    if (!mapped) return o;
    return { ...o, wallIndex: mapped.index, alongWall: o.alongWall + mapped.alongAdd };
  });
  const mountsNext = mounts.map((m) => {
    if (m.interiorWallId) return m;
    const mapped = mapEdge(m.wallIndex);
    if (!mapped) return m;
    return { ...m, wallIndex: mapped.index, alongWall: m.alongWall + mapped.alongAdd };
  });

  const locks: Record<number, number> = {};
  for (const [k, v] of Object.entries(base.lockedWallLengths ?? {})) {
    const edge = Number(k);
    if (edge === index || edge === prev) continue;
    const mapped = mapEdge(edge);
    if (mapped) locks[mapped.index] = v;
  }

  const roomNext: Room = { ...base, points, pointIds };
  putLockMap(roomNext, "lockedWallLengths", Object.keys(locks).length ? locks : undefined);
  return {
    room: roomNext,
    openings: openingsNext,
    mounts: mountsNext,
  };
}

function toWork(room: Room, openings: Opening[], mounts: WallMount[]): Work {
  const base = ensurePointIds(room);
  return {
    points: base.points.map((p) => ({ ...p })),
    pointIds: [...(base.pointIds ?? [])],
    interiorVertices: (base.interiorVertices ?? []).map((v) => ({ ...v })),
    interiorWalls: (base.interiorWalls ?? []).map((w) => ({
      id: w.id,
      vertexIds: [...w.vertexIds],
    })),
    openings: openings.map((o) => ({ ...o })),
    mounts: mounts.map((m) => ({ ...m })),
    lockedWallLengths: base.lockedWallLengths ? { ...base.lockedWallLengths } : undefined,
    lockedInteriorLengths: base.lockedInteriorLengths
      ? { ...base.lockedInteriorLengths }
      : undefined,
    junctions: new Set<string>(),
  };
}

export function deleteInteriorWall(
  room: Room,
  openings: Opening[],
  mounts: WallMount[],
  wallId: string
): { room: Room; openings: Opening[]; mounts: WallMount[] } {
  const walls = (room.interiorWalls ?? []).filter((w) => w.id !== wallId);
  const used = new Set<string>();
  for (const w of walls) for (const id of w.vertexIds) used.add(id);
  const pointIds = new Set(room.pointIds ?? []);
  const vertices = (room.interiorVertices ?? []).filter(
    (v) => used.has(v.id) || pointIds.has(v.id)
  );
  const locks: Record<string, number> = {};
  for (const [k, v] of Object.entries(room.lockedInteriorLengths ?? {})) {
    if (!k.startsWith(`${wallId}:`)) locks[k] = v;
  }
  const roomNext: Room = { ...room, interiorWalls: walls, interiorVertices: vertices };
  putLockMap(
    roomNext,
    "lockedInteriorLengths",
    Object.keys(locks).length ? locks : undefined
  );
  return {
    room: roomNext,
    openings: openings.filter((o) => o.interiorWallId !== wallId),
    mounts: mounts.filter((m) => m.interiorWallId !== wallId),
  };
}

export function addInteriorCorner(
  room: Room,
  openings: Opening[],
  mounts: WallMount[],
  wallId: string,
  segment: number
): { room: Room; openings: Opening[]; mounts: WallMount[]; vertexId: string } | null {
  const base = ensurePointIds(room);
  const wall = (base.interiorWalls ?? []).find((w) => w.id === wallId);
  if (!wall || segment < 0 || segment >= wall.vertexIds.length - 1) return null;
  const work = toWork(base, openings, mounts);
  const vertexId = splitInterior(work, wallId, segment, 0.5);
  if (!vertexId) return null;
  return {
    room: roomFromWork(base, work),
    openings: work.openings,
    mounts: work.mounts,
    vertexId,
  };
}

export function removeInteriorVertex(
  room: Room,
  openings: Opening[],
  mounts: WallMount[],
  vertexId: string
): { room: Room; openings: Opening[]; mounts: WallMount[] } | null {
  if ((room.pointIds ?? []).includes(vertexId)) return null;
  const walls = room.interiorWalls ?? [];
  const users = walls.filter((w) => w.vertexIds.includes(vertexId));
  if (users.length !== 1) return null;
  const wall = users[0];
  const idx = wall.vertexIds.indexOf(vertexId);
  if (idx < 0) return null;
  if (wall.vertexIds.length <= 2) {
    return deleteInteriorWall(room, openings, mounts, wall.id);
  }
  // Dropping a middle corner merges the two segments around it.
  const nextIds = wall.vertexIds.filter((id) => id !== vertexId);
  const a = vertexPosition(room, wall.vertexIds[Math.max(0, idx - 1)]);
  const mid = vertexPosition(room, vertexId);
  const firstLen = a && mid ? distance(a, mid) : 0;
  let nextOpenings = openings;
  let nextMounts = mounts;
  if (idx > 0 && idx < wall.vertexIds.length - 1) {
    nextOpenings = openings.map((o) => remapRemovedCorner(o, wall.id, idx, firstLen));
    nextMounts = mounts.map((m) => remapRemovedCorner(m, wall.id, idx, firstLen));
  } else {
    // Endpoint: segments after the dropped end shift down by one when the
    // first vertex goes away.
    const dropSeg = idx === 0 ? 0 : wall.vertexIds.length - 2;
    nextOpenings = openings
      .filter((o) => !(o.interiorWallId === wall.id && o.interiorSegment === dropSeg))
      .map((o) => shiftInteriorDown(o, wall.id, dropSeg));
    nextMounts = mounts
      .filter((m) => !(m.interiorWallId === wall.id && m.interiorSegment === dropSeg))
      .map((m) => shiftInteriorDown(m, wall.id, dropSeg));
  }
  const vertices = (room.interiorVertices ?? []).filter((v) => v.id !== vertexId);
  return {
    room: {
      ...room,
      interiorVertices: vertices,
      interiorWalls: walls.map((w) =>
        w.id === wall.id ? { ...w, vertexIds: nextIds } : w
      ),
    },
    openings: nextOpenings,
    mounts: nextMounts,
  };
}

function remapRemovedCorner<T extends Hosted>(
  item: T,
  wallId: string,
  removedIndex: number,
  firstLen: number
): T {
  if (item.interiorWallId !== wallId || item.interiorSegment == null) return item;
  const seg = item.interiorSegment;
  // Segments: 0..removedIndex-2 stay, removedIndex-1 absorbs removedIndex.
  const merged = removedIndex - 1;
  if (seg === merged) return item;
  if (seg === removedIndex) {
    return { ...item, interiorSegment: merged, alongWall: item.alongWall + firstLen };
  }
  if (seg > removedIndex) return { ...item, interiorSegment: seg - 1 };
  return item;
}

function shiftInteriorDown<T extends Hosted>(item: T, wallId: string, dropSeg: number): T {
  if (item.interiorWallId !== wallId || item.interiorSegment == null) return item;
  if (item.interiorSegment > dropSeg) {
    return { ...item, interiorSegment: item.interiorSegment - 1 };
  }
  return item;
}

export function moveVertexById(room: Room, id: string, next: Vec2): Room {
  const ids = room.pointIds ?? [];
  const bi = ids.indexOf(id);
  if (bi >= 0) {
    return {
      ...room,
      points: room.points.map((p, i) => (i === bi ? { x: next.x, y: next.y } : p)),
    };
  }
  return {
    ...room,
    interiorVertices: (room.interiorVertices ?? []).map((v) =>
      v.id === id ? { ...v, x: next.x, y: next.y } : v
    ),
  };
}

type HostLine = {
  kind: "line";
  a: Vec2;
  b: Vec2;
};

type HostPin = { kind: "pinned" } | { kind: "free" } | HostLine;

function hostConstraint(room: Room, vertexId: string, excludeWallId: string): HostPin {
  const ids = room.pointIds ?? [];
  const bi = ids.indexOf(vertexId);
  if (bi >= 0) {
    const n = room.points.length;
    const prev = room.points[(bi - 1 + n) % n];
    const cur = room.points[bi];
    const next = room.points[(bi + 1) % n];
    if (isColinear(prev, cur, next)) return { kind: "line", a: prev, b: next };
    return { kind: "pinned" };
  }
  for (const wall of room.interiorWalls ?? []) {
    if (wall.id === excludeWallId) continue;
    const idx = wall.vertexIds.indexOf(vertexId);
    if (idx < 0) continue;
    const prevId = wall.vertexIds[idx - 1];
    const nextId = wall.vertexIds[idx + 1];
    const prev = prevId ? vertexPosition(room, prevId) : null;
    const next = nextId ? vertexPosition(room, nextId) : null;
    const cur = vertexPosition(room, vertexId);
    if (prev && next && cur && isColinear(prev, cur, next)) {
      return { kind: "line", a: prev, b: next };
    }
    if (prev && cur && !next) return { kind: "line", a: prev, b: cur };
    if (next && cur && !prev) return { kind: "line", a: cur, b: next };
    if (prev && next && cur) {
      // Corner of another wall: slide along the longer adjacent segment.
      const da = distance(prev, cur);
      const db = distance(cur, next);
      return da >= db
        ? { kind: "line", a: prev, b: cur }
        : { kind: "line", a: cur, b: next };
    }
  }
  return { kind: "free" };
}

function isColinear(a: Vec2, b: Vec2, c: Vec2): boolean {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const bcx = c.x - b.x;
  const bcy = c.y - b.y;
  const cross = abx * bcy - aby * bcx;
  const la = Math.hypot(abx, aby);
  const lb = Math.hypot(bcx, bcy);
  if (la < 1e-6 || lb < 1e-6) return true;
  return Math.abs(cross) / (la * lb) < 1e-3;
}

function clampToHost(p: Vec2, host: HostLine, margin: number): Vec2 {
  const len = distance(host.a, host.b);
  if (len < margin * 2 + 1) {
    return lerp(host.a, host.b, 0.5);
  }
  let t = projectT(p, host.a, host.b);
  const mt = margin / len;
  t = Math.max(mt, Math.min(1 - mt, t));
  return lerp(host.a, host.b, t);
}

function bestOnHost(
  pinned: Vec2,
  current: Vec2,
  host: HostLine,
  targetLen: number
): Vec2 {
  const len = distance(host.a, host.b);
  const margin = len > HOST_MARGIN_IN * 2 + 2 ? HOST_MARGIN_IN : 1;
  const lo = margin / Math.max(len, 1);
  const hi = 1 - lo;
  const candidates: Vec2[] = [lerp(host.a, host.b, lo), lerp(host.a, host.b, hi)];
  const dx = host.b.x - host.a.x;
  const dy = host.b.y - host.a.y;
  const gx = host.a.x - pinned.x;
  const gy = host.a.y - pinned.y;
  const a = dx * dx + dy * dy;
  const b = 2 * (gx * dx + gy * dy);
  const c = gx * gx + gy * gy - targetLen * targetLen;
  const disc = b * b - 4 * a * c;
  if (a > 1e-9 && disc >= 0) {
    const s = Math.sqrt(disc);
    for (const t of [(-b - s) / (2 * a), (-b + s) / (2 * a)]) {
      if (t >= lo && t <= hi) candidates.push(lerp(host.a, host.b, t));
    }
  }
  const footT = Math.max(lo, Math.min(hi, projectT(pinned, host.a, host.b)));
  candidates.push(lerp(host.a, host.b, footT));

  let best = clampToHost(current, host, margin);
  let bestKey = Infinity;
  for (const cand of candidates) {
    const score = Math.abs(distance(pinned, cand) - targetLen);
    const key = score * 1e6 + distance(cand, current);
    if (key < bestKey) {
      bestKey = key;
      best = cand;
    }
  }
  return best;
}

export function resizeInteriorSegment(
  room: Room,
  wallId: string,
  segment: number,
  newLength: number
): Room {
  const seg = interiorSegments(room).find(
    (s) => s.wallId === wallId && s.segment === segment
  );
  if (!seg) return room;
  const target = Math.max(INTERIOR_MIN_LENGTH_IN, newLength);
  const bHost = hostConstraint(room, seg.bId, wallId);
  const aHost = hostConstraint(room, seg.aId, wallId);

  if (bHost.kind === "free") {
    const dir = wallDirection(seg.a, seg.b);
    return moveVertexById(room, seg.bId, {
      x: seg.a.x + dir.x * target,
      y: seg.a.y + dir.y * target,
    });
  }
  if (bHost.kind === "line") {
    return moveVertexById(room, seg.bId, bestOnHost(seg.a, seg.b, bHost, target));
  }
  if (aHost.kind === "free") {
    const dir = wallDirection(seg.b, seg.a);
    return moveVertexById(room, seg.aId, {
      x: seg.b.x + dir.x * target,
      y: seg.b.y + dir.y * target,
    });
  }
  if (aHost.kind === "line") {
    return moveVertexById(room, seg.aId, bestOnHost(seg.b, seg.a, aHost, target));
  }
  return room;
}

export function slideInteriorSegment(
  room: Room,
  wallId: string,
  segment: number,
  delta: number
): Room {
  const seg = interiorSegments(room).find(
    (s) => s.wallId === wallId && s.segment === segment
  );
  if (!seg) return room;
  const dir = wallDirection(seg.a, seg.b);
  const shift = { x: -dir.y * delta, y: dir.x * delta };
  const shiftedA = { x: seg.a.x + shift.x, y: seg.a.y + shift.y };
  const shiftedB = { x: seg.b.x + shift.x, y: seg.b.y + shift.y };
  const targetA = slideTarget(room, seg.aId, wallId, shiftedA, shiftedB, seg.a, shiftedA);
  const targetB = slideTarget(room, seg.bId, wallId, shiftedA, shiftedB, seg.b, shiftedB);
  let next = moveVertexById(room, seg.aId, targetA);
  next = moveVertexById(next, seg.bId, targetB);
  return next;
}

function slideTarget(
  room: Room,
  vertexId: string,
  wallId: string,
  lineA: Vec2,
  lineB: Vec2,
  current: Vec2,
  shifted: Vec2
): Vec2 {
  const host = hostConstraint(room, vertexId, wallId);
  if (host.kind === "pinned") return current;
  if (host.kind === "free") return shifted;
  const hit = lineLine(lineA, lineB, host.a, host.b);
  const margin = distance(host.a, host.b) > HOST_MARGIN_IN * 2 + 2 ? HOST_MARGIN_IN : 1;
  if (!hit) return clampToHost(shifted, host, margin);
  return clampToHost(hit, host, margin);
}

function lineLine(a: Vec2, b: Vec2, c: Vec2, d: Vec2): Vec2 | null {
  const rx = b.x - a.x;
  const ry = b.y - a.y;
  const sx = d.x - c.x;
  const sy = d.y - c.y;
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) < 1e-9) return null;
  const t = ((c.x - a.x) * sy - (c.y - a.y) * sx) / denom;
  return { x: a.x + t * rx, y: a.y + t * ry };
}
